import { useTranslation } from '../../../lib/i18n';
import { messageDir } from '../../../lib/message-direction';
import type { SentOutreachSummary } from '../../../lib/use-sent-outreaches';
import { Badge, Card, Typography } from '../ui';
import { useContactBook } from '../../../lib/contact-book';
import { contactDisplayName } from '../../../lib/contact-rows';
import { threadByOutreachId, threadStage } from '../../../lib/outreach-stage';
import { sentOutreachRows } from '../../../lib/outreach-thread-view';
import { renderThreadLabel } from '../../../lib/outreach-thread-view-render';
import { useAgentName } from '../../../lib/use-agent-name';
import { useThreadOwnerNames } from '../outreach/OutreachThreadTranscript';
import { StageDot } from '../contacts/StageDot';
import { StageLine } from '../contacts/StageLine';
import styles from './TaskSentOutreachSection.module.css';

/**
 * What this task said to the people it reached out to — and what became of it.
 *
 * A task that messaged somebody used to end at "Done" and say nothing about the outcome — "Done" means
 * *the agent sent it*, not *they answered*, and the detail gave no way to tell those apart. The one fact
 * worth leading with is still **whether they wrote back**, so it is a badge rather than a line of prose:
 * `waiting-on-a-reply` while the thread is open and unanswered, `done` once they have replied.
 *
 * **Under it now sits the CORRESPONDENCE ITSELF** — every message, attributed and dated — rather than a
 * two-line clamp of the last thing said. That was the reported gap, on a goal whose tasks had each sent a
 * real WhatsApp message to a colleague: "I don't see the messages that the agent sent to each. I was
 * expecting to see it in the agent conversation in each task." A single last line cannot answer *what did
 * it say*, and on a thread that got a reply the last line is THEIRS — so the one thing the user sent on
 * their own behalf was the one thing not on screen.
 *
 * Rendered IN PLACE, not behind a tap, and that is the requirement rather than a layout preference: the
 * person is reading the task precisely to find out what was sent in their name, so it must not be one
 * more click away. Their message sits at the inline start and the user's own at the inline end — the
 * shape every chat uses, so who spoke reads before a single word does, and it mirrors with the page in a
 * Hebrew thread instead of pinning a side to the left.
 *
 * Two honest limits, visible rather than papered over:
 *  - a message written before per-message dates existed has NO date, and renders without one instead of
 *    borrowing the thread's;
 *  - an outreach the service holds no messages for says so (`tasks.outreach.emptyThread`) rather than
 *    rendering an empty bubble list that reads as a rendering failure.
 *
 * **The stage comes from the contact book**, joined by outreach id (`threadByOutreachId` over the
 * `useContactBook('')` head, warmed on app mount): a hit draws the stage dot and line and lends each
 * message its typed author. Speakers are read by the SAME model every thread viewer uses
 * (`sentOutreachRows` → `threadRows` / `threadSpeakerLabel`, contract `native-apps/shared/outreach-thread-view/contract.json`),
 * so one speaker reads the same here as in the thread. A miss keeps the summary's own rule — "Awaiting reply" while open and unanswered,
 * Done once they wrote back — so the section never computes a second stage of its own.
 *
 * Renders nothing at all when the task sent no outreach — no title, no empty state — matching how the
 * Files section beside it behaves. A task that never messaged anybody is not a task with an empty
 * correspondence; it is a task this section has nothing to say about.
 *
 * The channel each one went out on is deliberately NOT shown. The summary carries it, but there is no
 * channel label set in the catalogs, and inventing one means eight keys across 37 bundles for a subtitle
 * — a decision worth making once for every surface that wants it.
 */
export function TaskSentOutreachSection({ outreaches }: { outreaches?: SentOutreachSummary[] }) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const ownerNames = useThreadOwnerNames();
  const book = useContactBook('', { enabled: !!outreaches?.length });
  if (!outreaches || outreaches.length === 0) return null;
  const contacts = book.kind === 'ready' ? book.contacts : [];
  const tr = t as (key: string, params?: Record<string, unknown>) => string;

  return (
    <section className={styles.section} aria-label={t('tasks.sentOutreach.title')}>
      <Typography variant="h4" component="h3">
        {t('tasks.sentOutreach.title')}
      </Typography>
      <div className={styles.list}>
        {outreaches.map((o) => {
          // The recipient's name when the sender's own assistant resolved one. Unlike the inbound side
          // there is no "Message from X:" prefix to fall back on, so an unresolved name says so plainly
          // rather than inventing a placeholder that reads like a real person.
          const who: string = o.recipientName?.trim() || t('tasks.sentOutreach.unnamedRecipient') || '';
          const messages = o.messages ?? [];
          const joined = threadByOutreachId(contacts, o.id);
          const contactName = joined ? contactDisplayName(joined.entry.contact, joined.entry.channels, who, o.recipientName) : who;
          // Who wrote each message — through the thread viewer's speaker model, so a run label reads exactly
          // as it does in the thread: the joined thread's own `author` / envelope when it holds the same
          // messages, else the side, with a message that is not ours read as the contact's (never an
          // unlabelled unknown). `sentOutreachRows` owns that rule (contract `sentOutreach`).
          const rows = sentOutreachRows(
            messages,
            joined?.thread.messages,
            { contactName, channel: joined?.thread.channel ?? o.channel?.toLowerCase(), ownerNames },
            new Date(),
          );
          return (
            <Card key={o.id} variant="stone" size="sm" className={styles.row}>
              <div className={styles.head}>
                <Typography variant="p2" component="span" noWrap className={styles.who} dir={messageDir(who ?? '')}>
                  {who}
                </Typography>
                {/* Open and unanswered vs. they replied. A settled thread that never got an answer shows
                    neither — it is closed, and "no reply yet" would be a claim about a finished exchange. */}
                {joined ? (
                  <StageDot stage={threadStage(joined.thread)} />
                ) : o.awaitingReply ? (
                  <Badge variant="waiting-on-a-reply">{t('tasks.sentOutreach.awaitingReply')}</Badge>
                ) : o.replied ? (
                  <Badge variant="done">{t('tasks.status.done')}</Badge>
                ) : null}
              </div>
              {joined ? <StageLine thread={joined.thread} contactName={contactName} /> : null}

              {messages.length === 0 ? (
                // The link to this outreach resolved, but the service holds no messages on it. Say that,
                // rather than an empty bubble list — and never fall back to `lastMessage`, which would be
                // a message shown under a claim that there are none.
                <Typography variant="p2" component="p" color="secondary" className={styles.empty}>
                  {t('tasks.outreach.emptyThread')}
                </Typography>
              ) : (
                <ol className={styles.thread}>
                  {messages.map((m, i) => {
                    const row = rows[i];
                    const view = row && 'side' in row ? row : null;
                    // The SIDE comes from the same speaker decision as the label, so the two never disagree.
                    const mine = view ? view.side === 'mine' : m.mine;
                    return (
                      <li key={i} className={mine ? `${styles.message} ${styles.mine}` : styles.message}>
                        {view?.showLabel ? (
                          <Typography variant="p3" color="secondary">
                            {renderThreadLabel(view.label, tr, agent.name)}
                          </Typography>
                        ) : null}
                        <p className={styles.text} dir={messageDir(m.text)}>
                          {m.text}
                        </p>
                        {/* Relative is what you scan a thread with; the exact instant rides along in
                          `title` for a pointer. A message with no recorded date shows nothing rather
                          than borrowing the thread's — see the header. */}
                        {m.relative ? (
                          <span className={styles.when} title={m.at ? new Date(m.at).toLocaleString() : undefined}>
                            {m.relative}
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ol>
              )}
            </Card>
          );
        })}
      </div>
    </section>
  );
}
